import {Injectable} from '@angular/core';
import {HttpService} from '../http.service';
import {AbstractRestService} from '../../shared/types/AbstractRestService';
import {Observable, of, throwError} from 'rxjs';
import {AppSettingsService} from '../app-settings.service';
import {catchError, map, switchMap} from 'rxjs/operators';
import {BalanceDto, ExternalClientBankAccountDto} from '../../dashboard/bankAccounts/models/externalClientBankAccountDto.model';
import {LinkTokenReadDto} from '../../shared/models/cre-banking/plaid/LinkTokenReadDto.model';
import {AchDebitCreateDto} from '../../shared/models/cre-banking/plaid/AchDebitCreateDto.model';
import {AccessTokenCreateDto} from '../../shared/models/cre-banking/plaid/AccessTokenCreateDto.model';
import {PlaidTokenCreationMode} from 'src/app/shared/enums/PlaidTokenCreationMode.enum';

@Injectable()
export class PlaidDataService extends AbstractRestService<any, any> {
  protected readonly baseEndpoint$ = this.appSettingsService.localEnv ?
    of('https://localhost:44360/api/plaid/') :
    of('plaid/');

  constructor(protected http: HttpService,
    private appSettingsService: AppSettingsService) {
    super(http);
  }

  /**
   * Get a link token to init link process
   */
  public getLinkToken(clientBankAccountId: number = null, accessTokenId: string | null = null): Observable<LinkTokenReadDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<LinkTokenReadDto>(`${baseUrl}link-token${!!clientBankAccountId ? ('/' + clientBankAccountId) : ''}?creationMode=${PlaidTokenCreationMode.Funding}${(!!accessTokenId ? `&accessTokenId=${accessTokenId}` : '')}`)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  /**
   * Exchange public token with access token
   * @param requestData request data for exchanging the public token
   */
  public exchangeToken(requestData: AccessTokenCreateDto): Observable<boolean> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.postTyped<boolean>(`${baseUrl}exchange`, requestData)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  /**
   * Get a list of existing linked bank accounts
   */
  public getLinkedBankAccounts(holdingId?: number, retrieveBalance?: boolean, unitAccountId?: number): Observable<ExternalClientBankAccountDto[]> {
    const queryString = `?retrieveBalance=${retrieveBalance}${(!!holdingId ? `&holdingId=${holdingId}` : '')}${(!!unitAccountId ? `&unitAccountId=${unitAccountId}` : '')}`
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<ExternalClientBankAccountDto[]>(`${baseUrl}linked-accounts${queryString}`)),
      catchError(err => {
        return throwError(err);
      }));
  }

  public getLinkedBankAccountBalance(externalAccountId: string, accessTokenId: string): Observable<number> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<BalanceDto>(`${baseUrl}linked-account/${externalAccountId}/balance?accessTokenId=${accessTokenId}`)),
      map(b => b.available));
  }

  public disconnectLinkedBankAccounts(clientBankAccountId: number): Observable<any> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.putTyped<any>(`${baseEndpoint}disconnect/${clientBankAccountId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    )
  }

  public reconnectLinkedBankAccounts(clientBankAccountId: number): Observable<any> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.putTyped<any>(`${baseEndpoint}reconnect/${clientBankAccountId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    )
  }

  public deleteAccessToken(accessTokenId: string): Observable<any> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.deleteTyped<any>(`${baseEndpoint}delete?accessTokenId=${accessTokenId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    )
  }

  /**
   * Transfer funds from linked Plaid bank account to Unit bank account
   * @param transferUsingPlaidCreateDto Transfer request data
   */
  public transferFromLinkedPlaidBankAccount(transferUsingPlaidCreateDto: AchDebitCreateDto) {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.postTyped<any>(`${baseUrl}transfer`, transferUsingPlaidCreateDto)),
      catchError(err => {
        return throwError(err);
      })
    );
  }
}
